import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ArrowLeft, Loader2, Save, Facebook, Instagram, Twitter, Youtube, Linkedin, MessageCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSocialLinks = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [socialData, setSocialData] = useState({
    facebook: '',
    instagram: '',
    twitter: '',
    youtube: '',
    linkedin: '',
    whatsapp: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchSocialLinks();
  }, [navigate]);

  const fetchSocialLinks = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/social-links`);
      setSocialData(response.data);
    } catch (error) {
      console.error('Error fetching social links:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('admin_token');

    try {
      await axios.put(`${BACKEND_URL}/api/admin/social-links`, socialData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Social Links Saved",
        description: "All social links updated successfully"
      });
    } catch (error) {
      toast({
        title: "Save Failed",
        description: error.response?.data?.detail || "Failed to save social links",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const socialFields = [
    { key: 'facebook', icon: Facebook, label: 'Facebook', placeholder: 'https://facebook.com/yourpage', color: 'text-blue-600' },
    { key: 'instagram', icon: Instagram, label: 'Instagram', placeholder: 'https://instagram.com/yourprofile', color: 'text-pink-600' },
    { key: 'twitter', icon: Twitter, label: 'Twitter / X', placeholder: 'https://twitter.com/yourhandle', color: 'text-sky-500' },
    { key: 'youtube', icon: Youtube, label: 'YouTube', placeholder: 'https://youtube.com/yourchannel', color: 'text-red-600' },
    { key: 'linkedin', icon: Linkedin, label: 'LinkedIn', placeholder: 'https://linkedin.com/company/yourcompany', color: 'text-blue-700' },
    { key: 'whatsapp', icon: MessageCircle, label: 'WhatsApp', placeholder: 'https://wa.me/919876543210', color: 'text-green-500' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/admin/dashboard">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Social Links</h1>
                <p className="text-sm text-gray-600">Configure social media links</p>
              </div>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-orange-500 to-orange-600"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Card className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-6">Social Media Links</h3>
          <p className="text-sm text-gray-600 mb-6">
            Add your social media profile URLs. Leave empty to hide the icon on the website.
          </p>
          
          <div className="space-y-6">
            {socialFields.map(({ key, icon: Icon, label, placeholder, color }) => (
              <div key={key} className="flex items-center space-x-4">
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 ${color}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                  </label>
                  <Input
                    value={socialData[key]}
                    onChange={(e) => setSocialData({...socialData, [key]: e.target.value})}
                    placeholder={placeholder}
                    data-testid={`social-input-${key}`}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Tip:</strong> For WhatsApp, use the format: <code className="bg-blue-100 px-1 rounded">https://wa.me/919876543210</code> (with country code, no spaces or dashes)
            </p>
          </div>
        </Card>

        {/* Save Button at Bottom */}
        <div className="mt-8 text-center">
          <Button
            onClick={handleSave}
            disabled={saving}
            size="lg"
            className="bg-gradient-to-r from-orange-500 to-orange-600 px-12"
          >
            {saving ? 'Saving...' : 'Save Social Links'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminSocialLinks;
